import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Package, Search, Truck, Check, Clock, CheckCircle2, XCircle, PackageOpen, PhoneCall, MapPin, MessageCircle } from "lucide-react";
import apiClient, { formatApiErrorDetail } from "@/utils/api";
import { useSiteSettings } from "@/context/SiteSettingsContext";

const STATUS_STEPS = [
  { key: "pending", label: "Order Placed", icon: Clock, description: "We received your order" },
  { key: "confirmed", label: "Confirmed", icon: Check, description: "Order confirmed & queued" },
  { key: "processing", label: "Processing", icon: PackageOpen, description: "Packing your complete set" },
  { key: "shipped", label: "Shipped", icon: Truck, description: "Dispatched via courier" },
  { key: "delivered", label: "Delivered", icon: CheckCircle2, description: "Successfully delivered" },
];

const stepIndex = (s) => {
  const idx = STATUS_STEPS.findIndex((x) => x.key === s);
  return idx === -1 ? 0 : idx;
};

export default function OrderTracking() {
  const { settings } = useSiteSettings();
  const [orderNumber, setOrderNumber] = useState("");
  const [mobile, setMobile] = useState("");
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [postexJourney, setPostexJourney] = useState(null);
  const [postexLoading, setPostexLoading] = useState(false);

  const handleTrack = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setOrder(null);
    setPostexJourney(null);
    try {
      const { data } = await apiClient.post("/orders/track", {
        order_number: orderNumber.trim().toUpperCase(),
        mobile_number: mobile.trim(),
      });
      setOrder(data);
      if (data.tracking_number) {
        setPostexLoading(true);
        try {
          const { data: pjd } = await apiClient.get(`/orders/postex-track/${encodeURIComponent(data.tracking_number)}`);
          setPostexJourney(pjd);
        } catch (_e) {
          // Silent fallback: keep base tracking number visible even if PostEx is slow.
        } finally {
          setPostexLoading(false);
        }
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not track order. Please verify details.");
    } finally {
      setLoading(false);
    }
  };

  const status = order?.order_status || "pending";
  const cancelled = status === "cancelled";
  const currentIdx = stepIndex(status);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      {/* Hero */}
      <div className="text-center space-y-2">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 text-[#996515] text-[11px] font-bold uppercase tracking-widest">
          <Package className="w-3.5 h-3.5" />
          <span>Order Tracking</span>
        </div>
        <h1 data-testid="track-order-title" className="font-heading text-3xl sm:text-4xl font-bold text-stone-900">
          Track your Anwer Fancy Lace order
        </h1>
        <p className="text-sm text-stone-500 max-w-lg mx-auto">
          Enter your order number and the mobile number you used at checkout. We keep your address and items private to you.
        </p>
      </div>

      {/* Form */}
      <form
        onSubmit={handleTrack}
        className="bg-white p-6 sm:p-8 rounded-3xl border border-[#E5E0D8] shadow-luxury space-y-4"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-xs font-bold uppercase tracking-wider text-stone-800">Order Number</label>
            <input
              type="text"
              required
              data-testid="track-order-number-input"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              placeholder="AFL-202609-XXXX"
              className="w-full px-3.5 py-2.5 text-sm bg-[#FAF8F5] border border-stone-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold uppercase tracking-wider text-stone-800">Mobile / WhatsApp</label>
            <input
              type="tel"
              required
              data-testid="track-order-mobile-input"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="03XX XXXXXXX"
              className="w-full px-3.5 py-2.5 text-sm bg-[#FAF8F5] border border-stone-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
            />
          </div>
        </div>

        {error && (
          <div data-testid="track-order-error" className="text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="track-order-submit-btn"
          className="w-full sm:w-auto px-6 py-3 bg-[#1C1917] hover:bg-[#D4AF37] text-white hover:text-[#1C1917] text-xs font-bold rounded-xl shadow-md transition flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <Search className="w-4 h-4" />
          <span>{loading ? "Tracking…" : "Track My Order"}</span>
        </button>
      </form>

      {/* Result */}
      {order && (
        <div data-testid="track-order-result" className="bg-white rounded-3xl border border-[#E5E0D8] shadow-luxury overflow-hidden">
          {/* Header row */}
          <div className="bg-[#1C1917] text-white px-6 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <span className="text-[11px] uppercase tracking-widest text-[#E6C687] font-semibold">Order</span>
              <div className="font-heading text-xl font-bold">{order.order_number}</div>
              <div className="text-[11px] text-stone-400">
                Placed on {new Date(order.created_at).toLocaleDateString()} • {order.city}
              </div>
            </div>
            <div className="text-right">
              <span className="text-[11px] uppercase tracking-widest text-stone-400">Current Status</span>
              <div
                data-testid="track-order-status-badge"
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold mt-1 ${
                  cancelled
                    ? "bg-rose-500/20 text-rose-200 border border-rose-500/50"
                    : "bg-[#D4AF37]/20 text-[#E6C687] border border-[#D4AF37]/50"
                }`}
              >
                {cancelled ? <XCircle className="w-3.5 h-3.5" /> : <Truck className="w-3.5 h-3.5" />}
                <span className="capitalize">{status.replace("_", " ")}</span>
              </div>
            </div>
          </div>

          {/* Progress */}
          {cancelled ? (
            <div className="p-6 text-center text-sm text-rose-800 bg-rose-50 border-t border-rose-200">
              This order has been cancelled. Please contact us if this is unexpected.
            </div>
          ) : (
            <div className="p-6 sm:p-8 space-y-4">
              <ol className="relative grid grid-cols-1 sm:grid-cols-5 gap-6 sm:gap-2">
                {STATUS_STEPS.map((step, i) => {
                  const done = i <= currentIdx;
                  const Icon = step.icon;
                  return (
                    <li
                      key={step.key}
                      data-testid={`track-step-${step.key}`}
                      className="flex sm:flex-col items-start sm:items-center gap-3 sm:gap-2 relative"
                    >
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 border-2 transition ${
                          done
                            ? "bg-[#D4AF37] border-[#D4AF37] text-[#1C1917]"
                            : "bg-white border-stone-200 text-stone-400"
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="text-left sm:text-center">
                        <div className={`text-xs font-bold ${done ? "text-stone-900" : "text-stone-400"}`}>
                          {step.label}
                        </div>
                        <div className="text-[10px] text-stone-500 hidden sm:block">{step.description}</div>
                      </div>
                    </li>
                  );
                })}
              </ol>

              {order.tracking_number && (
                <div className="pt-4 border-t border-[#E5E0D8] text-xs space-y-3" data-testid="track-order-postex-block">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-stone-500">PostEx Tracking #: </span>
                    <span className="font-bold text-stone-900 font-mono" data-testid="track-order-courier-tracking">
                      {order.tracking_number}
                    </span>
                    {postexLoading && (
                      <span className="text-[10px] text-stone-400 italic">Fetching live status…</span>
                    )}
                  </div>

                  {postexJourney?.ok && postexJourney?.transaction_status && (
                    <div className="p-3 bg-[#FAF8F5] rounded-lg border border-[#E5E0D8]" data-testid="track-order-postex-live-status">
                      <div className="flex items-center gap-2 mb-2">
                        <Truck className="w-3.5 h-3.5 text-[#996515]" />
                        <span className="font-bold text-stone-900 text-[11px] uppercase tracking-wider">
                          Live Courier Status:
                        </span>
                        <span className="px-2 py-0.5 rounded-md bg-[#D4AF37]/15 text-[#996515] text-[10px] font-bold uppercase">
                          {postexJourney.transaction_status}
                        </span>
                      </div>
                      {postexJourney.order_pickup_date && (
                        <div className="text-[10px] text-stone-500">
                          Picked up on {postexJourney.order_pickup_date}
                        </div>
                      )}
                      {postexJourney.order_delivery_date && (
                        <div className="text-[10px] text-emerald-700 font-semibold">
                          Delivered {postexJourney.order_delivery_date}
                        </div>
                      )}
                      {Array.isArray(postexJourney.history) && postexJourney.history.length > 0 && (
                        <ol
                          className="mt-3 space-y-1 border-l border-stone-300 pl-3"
                          data-testid="track-order-postex-history"
                        >
                          {postexJourney.history.map((h, i) => (
                            <li key={i} className="relative text-[10px] text-stone-600">
                              <span className="absolute -left-[15px] top-1 w-2 h-2 rounded-full bg-[#D4AF37]" />
                              <span className="font-semibold text-stone-800">{h.message}</span>
                              {h.code && <span className="text-stone-400"> · {h.code}</span>}
                            </li>
                          ))}
                        </ol>
                      )}
                    </div>
                  )}

                  {postexJourney && !postexJourney.ok && (
                    <div className="text-[10px] text-stone-500 italic">
                      Live courier update unavailable right now. Use your tracking number on postex.pk if urgent.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Items and totals */}
          <div className="border-t border-[#E5E0D8] p-6 sm:p-8 grid grid-cols-1 md:grid-cols-2 gap-6 text-xs">
            <div className="space-y-3">
              <h4 className="font-bold text-stone-900 uppercase tracking-wider text-[11px]">Items</h4>
              <ul className="space-y-2">
                {(order.items || []).map((it, idx) => (
                  <li key={idx} className="flex items-start gap-2 p-2 bg-[#FAF8F5] rounded-lg border border-stone-200">
                    <div className="flex-1">
                      <div className="font-semibold text-stone-900 leading-snug">{it.product_name}</div>
                      <div className="text-[11px] text-stone-500">
                        {it.quantity} × Rs. {(it.price || 0).toLocaleString()}
                        {it.selected_color ? ` • ${it.selected_color}` : ""}
                      </div>
                    </div>
                    <div className="font-bold text-stone-900 whitespace-nowrap">
                      Rs. {(it.item_total || 0).toLocaleString()}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-3">
              <h4 className="font-bold text-stone-900 uppercase tracking-wider text-[11px]">Summary</h4>
              <div className="space-y-1.5 text-stone-700">
                <div className="flex justify-between"><span>Subtotal</span><span>Rs. {(order.subtotal || 0).toLocaleString()}</span></div>
                <div className="flex justify-between"><span>Delivery</span><span>{order.shipping_fee ? `Rs. ${order.shipping_fee.toLocaleString()}` : "FREE"}</span></div>
                <div className="flex justify-between pt-2 border-t border-stone-200 text-sm font-bold text-stone-900">
                  <span>Total</span>
                  <span data-testid="track-order-total">Rs. {(order.total_amount || 0).toLocaleString()}</span>
                </div>
                <div className="text-[11px] text-stone-500 pt-1">
                  Payment: <span className="font-semibold uppercase">{order.payment_method}</span> ({order.payment_status})
                </div>
              </div>

              <div className="pt-3 border-t border-stone-200 space-y-1.5 text-[11px] text-stone-500">
                <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3 text-[#D4AF37]" /> {order.delivery_address}</div>
                <a
                  href={`https://wa.me/${(settings.whatsapp_number || "").replace(/[^0-9]/g, "")}?text=Hello,%20I%20have%20a%20question%20about%20order%20${encodeURIComponent(order.order_number)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-emerald-700 hover:underline font-semibold pt-1"
                >
                  <MessageCircle className="w-3.5 h-3.5" />
                  <span>Contact us about this order on WhatsApp</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Help */}
      <div className="text-center text-xs text-stone-500 space-y-1">
        <p>Can’t find your order number? Check the confirmation SMS or WhatsApp message we sent when you ordered.</p>
        <p>
          Still stuck?{" "}
          <Link to="/contact" className="text-[#996515] font-semibold hover:underline">
            Contact our team
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
